import React from 'react';
import { SkewButton, SectionTitle, Card, GradientDivider } from '../components/UIComponents';
import { Dumbbell, Video, ShoppingBag, Calendar, Users, Zap, Play, Clock, Star, Crown, Check, Sparkles } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_goalgeous-hub/artifacts/ad5dhro0_Untitled%20design.png";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1623804421482-721b3901c125?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwxfHxuZW9uJTIwZ3ltJTIwbGlnaHRpbmclMjBlbmVyZ2V0aWMlMjBmaXRuZXNzJTIwYXRtb3NwaGVyZXxlbnwwfHx8fDE3NzA3NjM2MTJ8MA&ixlib=rb-4.1.0&q=85",
  training: "https://images.unsplash.com/photo-1616803824305-a07cfbc8ea60?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHw0fHxzdHJvbmclMjBhZnJpY2FuJTIwYW1lcmljYW4lMjB3b21hbiUyMGZpdG5lc3MlMjBneW0lMjB3b3Jrb3V0fGVufDB8fHx8MTc3MDc2MzU5MXww&ixlib=rb-4.1.0&q=85",
  merch: "https://images.unsplash.com/photo-1689007662824-434fb2f0ccee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxmaXRuZXNzJTIwbWVyY2hhbmRpc2UlMjBob29kaWUlMjB0LXNoaXJ0JTIwZ3ltJTIwd2VhciUyMGZsYXQlMjBsYXl8ZW58MHx8fHwxNzcwNzYzNjA1fDA&ixlib=rb-4.1.0&q=85",
};

const powerRoomPricing = [
  {
    name: "THE POWER ROOM – GRANDFATHERED RATE",
    price: "$69",
    period: "/month",
    highlight: true,
    bestValue: true,
    grandfatherRate: true,
    grandfatherNote: "Available to existing grandfathered members only.",
    features: [
      "Autopay monthly",
      "Unlimited group workouts",
      "No contract required",
      "Full community access"
    ]
  },
  {
    name: "CORE MEMBERSHIP",
    price: "$89",
    period: "/month",
    highlight: false,
    features: [
      "Autopay required",
      "Unlimited workouts",
      "Month-to-month flexibility",
      "Full community access"
    ]
  }
];

const classPacks = [
  { classes: 5, price: "$65", perClass: "$13" },
  { classes: 10, price: "$120", perClass: "$12" }
];

const coachingPackages = [
  {
    name: "Bronze Package",
    price: "$249",
    period: "/month",
    color: "amber",
    features: [
      "1 personal training session per week",
      "Unlimited access to THE POWER ROOM",
      "3-month commitment"
    ]
  },
  {
    name: "Silver Package",
    price: "$429",
    period: "/month",
    color: "gray",
    features: [
      "2 personal training sessions per week",
      "Unlimited classes",
      "3-month commitment"
    ]
  },
  {
    name: "Gold Package",
    price: "$599",
    period: "/month",
    color: "yellow",
    popular: true,
    features: [
      "3 personal training sessions per week",
      "Unlimited classes",
      "3-month commitment"
    ]
  }
];

const digitalPrograms = [
  {
    title: "30-Day Power Up Challenge",
    description: "Transform your body and mindset in 30 days with guided workouts and daily motivation.",
    price: "$49",
    duration: "30 days"
  },
  {
    title: "Strength Foundations",
    description: "Master the fundamentals of strength training with proper form and technique.",
    price: "$39",
    duration: "6 weeks"
  },
  {
    title: "HIIT Transformation",
    description: "High-intensity interval training program for maximum fat burn and cardio conditioning.",
    price: "$35",
    duration: "4 weeks"
  }
];

const prerecordedWorkouts = [
  { title: "Full Body Burn", duration: "45 min", level: "Intermediate", price: "$12" },
  { title: "Core Crusher", duration: "30 min", level: "All Levels", price: "$10" },
  { title: "Lower Body Blast", duration: "40 min", level: "Advanced", price: "$12" },
  { title: "Upper Body Sculpt", duration: "35 min", level: "Intermediate", price: "$10" },
];

const merchandise = [
  { name: "Power Up Your Fitness T-Shirts", price: "$30", image: IMAGES.merch },
  { name: "Power Up Your Fitness Hoodies", price: "$65", image: IMAGES.merch },
  { name: "Power Up Your Fitness Racerback Tanks", price: "$35", image: IMAGES.merch },
  { name: "Power Up Your Fitness Leggings", price: "$55", image: IMAGES.merch },
  { name: "Power Up Your Fitness Hats", price: "$28", image: IMAGES.merch },
];

export const PowerUpPage = () => {
  return (
    <div data-testid="powerup-page" className="bg-brand-black pt-20">
      {/* Hero Section */}
      <section data-testid="powerup-hero" className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={IMAGES.hero} 
            alt="Power Up Your Fitness" 
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-brand-black via-brand-black/90 to-brand-black/50" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-2xl">
            <p className="font-oswald text-sm uppercase tracking-[0.3em] text-brand-lime mb-4">
              The Main Hub
            </p>
            <h1 className="font-oswald text-5xl md:text-7xl font-bold uppercase tracking-tighter text-white mb-6">
              Power Up<br />
              <span className="text-brand-lime">Your Fitness</span>
            </h1>
            <GradientDivider />
            <p className="font-montserrat text-lg text-brand-gray leading-relaxed mt-8 mb-8">
              Your transformation headquarters. From THE POWER ROOM group training to personalized coaching, digital programs to pre-recorded workouts - everything you need to become the strongest version of yourself.
            </p>
            <SkewButton href="#power-room" testId="powerup-hero-cta">
              Join THE POWER ROOM
            </SkewButton>
          </div>
        </div>
      </section>

      {/* THE POWER ROOM Section */}
      <section id="power-room" data-testid="power-room-section" className="py-24 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="font-oswald text-sm uppercase tracking-[0.3em] text-brand-lime mb-4">
              Our Signature Experience
            </p>
            <h2 className="font-oswald text-5xl md:text-7xl font-bold uppercase tracking-tighter text-white mb-6">
              THE POWER <span className="text-brand-lime">ROOM</span>
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-brand-lime to-brand-pink mx-auto mb-8" />
            <p className="font-montserrat text-lg text-brand-gray leading-relaxed max-w-3xl mx-auto">
              High-energy group training built on discipline, consistency, and self-love. THE POWER ROOM is where women show up, push past limits, and grow stronger together in a supportive community. This is our signature group workout experience focused on accountability and confidence building.
            </p>
          </div>

          {/* Registration Fee */}
          <div className="bg-brand-black border border-brand-lime/30 p-6 mb-12 text-center">
            <p className="font-oswald text-lg uppercase tracking-wider text-brand-lime mb-2">Registration Fee</p>
            <p className="font-oswald text-3xl text-white">$35 <span className="text-brand-gray text-lg">one-time enrollment fee</span></p>
          </div>

          {/* Main Membership Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {powerRoomPricing.map((plan, index) => (
              <Card 
                key={plan.name}
                testId={`pricing-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`p-8 relative ${plan.bestValue ? 'border-brand-lime border-2' : ''}`}
              >
                {plan.bestValue && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-brand-lime text-brand-black px-4 py-1 font-oswald text-sm uppercase tracking-wider">
                      Best Value
                    </span>
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="font-oswald text-xl uppercase tracking-tight text-white mb-4">
                    {plan.name}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-oswald text-5xl text-brand-lime">{plan.price}</span>
                    <span className="font-montserrat text-brand-gray">{plan.period}</span>
                  </div>
                  {plan.grandfatherNote && (
                    <p className="text-sm text-brand-gray mt-2">{plan.grandfatherNote}</p>
                  )}
                  {plan.bestValue && !plan.grandfatherNote && (
                    <p className="text-sm text-brand-gray mt-2">Autopay monthly</p>
                  )}
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-brand-gray">
                      <Check size={16} className="text-brand-lime flex-shrink-0" />
                      <span className="font-montserrat text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                {plan.name === "THE POWER ROOM – GRANDFATHERED RATE" ? (
                  <a 
                    href="https://www.vagaro.com/cl/wViIxN0gi7uFOukv45Ehx6st1cQCf2DY6tHH8xOkq3I="
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`join-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="inline-flex items-center justify-center w-full gap-2 bg-brand-lime text-brand-black px-6 py-3 font-oswald uppercase tracking-wider text-sm transform -skew-x-6 hover:bg-brand-pink hover:text-white transition-all duration-300"
                  >
                    <span className="skew-x-6">Join Now</span>
                  </a>
                ) : (
                  <SkewButton 
                    variant="secondary"
                    href="https://viagro.com" 
                    testId={`join-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="w-full justify-center"
                  >
                    Join Now
                  </SkewButton>
                )}
              </Card>
            ))}
          </div>

          {/* Drop-in and Class Packs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Drop-in */}
            <Card testId="drop-in-pricing" className="p-6 text-center">
              <h4 className="font-oswald text-lg uppercase text-white mb-2">Drop-In</h4>
              <p className="font-oswald text-3xl text-brand-pink mb-2">$15</p>
              <p className="font-montserrat text-sm text-brand-gray">per class</p>
              <SkewButton 
                variant="outline" 
                href="https://viagro.com"
                testId="buy-drop-in"
                icon={false}
                className="mt-4 text-xs py-2 px-4"
              >
                Book Class
              </SkewButton>
            </Card>

            {/* Class Packs */}
            {classPacks.map((pack, index) => (
              <Card key={index} testId={`class-pack-${pack.classes}`} className="p-6 text-center">
                <h4 className="font-oswald text-lg uppercase text-white mb-2">{pack.classes} Class Pack</h4>
                <p className="font-oswald text-3xl text-brand-lime mb-1">{pack.price}</p>
                <p className="font-montserrat text-sm text-brand-gray">{pack.perClass}/class</p>
                <SkewButton 
                  variant="outline" 
                  href="https://viagro.com"
                  testId={`buy-pack-${pack.classes}`}
                  icon={false}
                  className="mt-4 text-xs py-2 px-4"
                >
                  Buy Pack
                </SkewButton>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Class Schedule CTA */}
      <section data-testid="class-schedule-section" className="py-16 bg-brand-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="bg-brand-dark border border-brand-lime/20 p-8 md:p-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              <div>
                <div className="flex items-center gap-4 mb-4">
                  <Calendar size={32} className="text-brand-lime" />
                  <h2 className="font-oswald text-3xl md:text-4xl uppercase text-white">
                    THE POWER ROOM Schedule
                  </h2>
                </div>
                <p className="font-montserrat text-brand-gray max-w-xl">
                  View our full class schedule and book your spot. From early morning power sessions to evening burn classes - find the perfect time to power up.
                </p>
              </div>
              <SkewButton href="https://viagro.com" testId="view-schedule-btn">
                View Schedule & Book
              </SkewButton>
            </div>
          </div>
        </div>
      </section>

      {/* POWER COACHING: 1:1 TRAINING Section */}
      <section data-testid="coaching-section" className="py-24 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <p className="font-oswald text-sm uppercase tracking-[0.3em] text-brand-pink mb-4">
              Personalized Support
            </p>
            <h2 className="font-oswald text-4xl md:text-6xl font-bold uppercase tracking-tighter text-white mb-6">
              POWER COACHING: <span className="text-brand-pink">1:1 Training</span>
            </h2>
            <div className="w-32 h-1 bg-gradient-to-r from-brand-pink to-brand-lime mx-auto mb-8" />
            <p className="font-montserrat text-lg text-brand-gray leading-relaxed max-w-3xl mx-auto">
              Personalized coaching for women who want more structure, guidance, and support alongside their group training. Designed to help maximize results and build confidence through focused attention.
            </p>
          </div>

          {/* Coaching Packages */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {coachingPackages.map((pkg, index) => (
              <Card 
                key={pkg.name}
                testId={`coaching-${pkg.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`p-8 relative ${pkg.popular ? 'border-brand-pink border-2' : ''}`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-brand-pink text-white px-4 py-1 font-oswald text-sm uppercase tracking-wider">
                      Most Popular
                    </span>
                  </div>
                )}
                <div className="text-center mb-6">
                  <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${
                    pkg.color === 'yellow' ? 'bg-yellow-500/20' : 
                    pkg.color === 'gray' ? 'bg-gray-400/20' : 
                    'bg-amber-600/20'
                  }`}>
                    <Crown size={28} className={
                      pkg.color === 'yellow' ? 'text-yellow-500' : 
                      pkg.color === 'gray' ? 'text-gray-400' : 
                      'text-amber-600'
                    } />
                  </div>
                  <h3 className="font-oswald text-xl uppercase tracking-tight text-white mb-4">
                    {pkg.name}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="font-oswald text-4xl text-brand-pink">{pkg.price}</span>
                    <span className="font-montserrat text-brand-gray">{pkg.period}</span>
                  </div>
                </div>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-brand-gray">
                      <Check size={16} className="text-brand-pink flex-shrink-0" />
                      <span className="font-montserrat text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <SkewButton 
                  variant="secondary"
                  href="https://viagro.com" 
                  testId={`enroll-${pkg.name.toLowerCase().replace(/\s+/g, '-')}`}
                  className="w-full justify-center"
                >
                  Get Started
                </SkewButton>
              </Card>
            ))}
          </div>

          {/* Single Sessions */}
          <div className="text-center bg-brand-black border border-white/10 p-8">
            <p className="font-oswald text-lg uppercase tracking-wider text-white mb-2">Single Sessions</p>
            <p className="font-montserrat text-brand-gray mb-4">Not ready for a package? Try a single session first.</p>
            <p className="font-oswald text-2xl text-brand-pink mb-4">Starting at $50 <span className="text-brand-gray text-lg">per session</span></p>
            <SkewButton 
              variant="outline"
              href="https://viagro.com" 
              testId="book-single-session"
            >
              Book a Session
            </SkewButton>
          </div>
        </div>
      </section>

      {/* Digital Programs Section */}
      <section data-testid="digital-programs-section" className="py-24 bg-brand-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <SectionTitle 
            title="Digital Programs" 
            subtitle="Train Anywhere"
            accent="lime"
            className="mb-16"
          />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {digitalPrograms.map((program, index) => (
              <Card 
                key={program.title}
                testId={`digital-program-${index}`}
                className="p-6"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Clock size={16} className="text-brand-lime" />
                  <span className="font-montserrat text-sm text-brand-gray">{program.duration}</span>
                </div>
                <h3 className="font-oswald text-xl uppercase text-white mb-3">
                  {program.title}
                </h3>
                <p className="font-montserrat text-sm text-brand-gray leading-relaxed mb-6">
                  {program.description}
                </p>
                <div className="flex items-center justify-between">
                  <span className="font-oswald text-2xl text-brand-lime">{program.price}</span>
                  <SkewButton 
                    variant="outline" 
                    href="https://viagro.com"
                    testId={`buy-program-${index}`}
                    icon={false}
                  >
                    Get Access
                  </SkewButton>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Pre-recorded Workouts */}
      <section data-testid="workouts-section" className="py-24 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <SectionTitle 
            title="Pre-Recorded Workouts" 
            subtitle="On-Demand"
            accent="pink"
            className="mb-16"
          />
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {prerecordedWorkouts.map((workout, index) => (
              <Card 
                key={workout.title}
                testId={`workout-${index}`}
                className="p-6 group cursor-pointer"
              >
                <div className="w-12 h-12 rounded-full bg-brand-pink/10 flex items-center justify-center mb-4 group-hover:bg-brand-pink/20 transition-colors">
                  <Play size={20} className="text-brand-pink" />
                </div>
                <h4 className="font-oswald text-lg uppercase text-white mb-2">
                  {workout.title}
                </h4>
                <div className="flex items-center gap-4 text-sm text-brand-gray mb-4">
                  <span>{workout.duration}</span>
                  <span className="text-brand-lime">{workout.level}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="font-oswald text-xl text-brand-pink">{workout.price}</span>
                  <SkewButton 
                    variant="outline" 
                    href="https://viagro.com"
                    testId={`buy-workout-${index}`}
                    icon={false}
                    className="text-xs py-2 px-4"
                  >
                    Buy
                  </SkewButton>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Merchandise Section */}
      <section data-testid="merch-section" className="py-24 bg-brand-black">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="mb-16">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8">
              <SectionTitle 
                title="Merchandise" 
                subtitle="Rep the Movement"
              />
              <SkewButton href="https://viagro.com" testId="shop-all-merch">
                Shop All
              </SkewButton>
            </div>
            <div className="max-w-3xl">
              <p className="font-oswald text-xl text-white mb-2">Rep The Movement. Wear The Power.</p>
              <p className="font-montserrat text-brand-gray leading-relaxed">
                This collection represents the heart of Power Up Your Fitness. Designed for women who train with purpose, move with intention, and live the lifestyle inside and outside of THE POWER ROOM.
              </p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-6">
            {merchandise.map((item, index) => (
              <Card 
                key={item.name}
                testId={`merch-item-${index}`}
                className="overflow-hidden group"
              >
                <div className="relative aspect-square overflow-hidden bg-brand-dark flex items-center justify-center">
                  <div className="w-16 h-16 rounded-full bg-brand-lime/10 flex items-center justify-center group-hover:bg-brand-lime/20 transition-colors">
                    <Zap size={32} className="text-brand-lime" />
                  </div>
                </div>
                <div className="p-4">
                  <h4 className="font-oswald text-xs uppercase text-white mb-1 leading-tight">{item.name}</h4>
                  <span className="font-montserrat text-brand-lime font-semibold text-sm">{item.price}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section data-testid="powerup-cta" className="py-24 bg-brand-dark relative">
        <div className="absolute inset-0 opacity-20">
          <img 
            src={IMAGES.training} 
            alt="Training" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <img 
            src={LOGO_URL} 
            alt="Power Up Your Fitness" 
            className="h-20 mx-auto mb-8"
          />
          <h2 className="font-oswald text-4xl md:text-5xl font-bold uppercase text-white mb-6">
            Enter <span className="text-brand-lime">THE POWER ROOM</span>
          </h2>
          <p className="font-montserrat text-lg text-brand-gray mb-8">
            This is where strength is built. This is where confidence returns. This is where women find themselves again.
          </p>
          <SkewButton href="https://viagro.com" testId="powerup-final-cta">
            Start Today
          </SkewButton>
        </div>
      </section>
    </div>
  );
};
